import React from "react";
import { CircleDollarSign, TrendingDown, TrendingUp, PieChart, ArrowRightLeft, Wallet } from "lucide-react";

// OptionStrat /build/ metrics strip — sits directly under the strategy
// header. Six tiles in a single row on desktop, 2-up grid on mobile.
//
// Props are intentionally permissive: missing fields render as "—" so the
// strip never breaks while strategies are still loading.

const fmt$ = (v) => {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  if (Math.abs(v) === Infinity) return v > 0 ? "Unlimited" : "Unlimited Loss";
  const sign = v < 0 ? "-" : "";
  const abs = Math.abs(v);
  if (abs >= 1000) return `${sign}$${abs.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  return `${sign}$${abs.toFixed(2)}`;
};

const fmtPct = (v) => {
  if (v === null || v === undefined || !Number.isFinite(v)) return "—";
  // Backend `metrics.pop` is already a percent value (e.g. 47.3) — no extra
  // ×100 scaling. Round to whole percent for the compact OptionStrat tile.
  return `${Math.round(v)}%`;
};

const Tile = ({ icon: Icon, label, value, tone = "white", testid }) => {
  const toneClass = {
    white: "text-white",
    green: "text-[#22C55E]",
    red: "text-[#EF4444]",
    yellow: "text-[#FACC15]",
    blue: "text-[#60A5FA]",
  }[tone];
  return (
    <div
      className="flex items-start gap-2.5 px-4 py-3 border-r border-white/5 last:border-r-0 min-w-0"
      data-testid={testid}
    >
      <Icon className={`w-3.5 h-3.5 mt-1 ${toneClass} flex-shrink-0`} />
      <div className="min-w-0 flex-1">
        <div className="text-[9.5px] uppercase tracking-[0.22em] text-white/45 mb-0.5 truncate">
          {label}
        </div>
        <div className={`num text-[15px] font-medium tabular-nums truncate ${toneClass}`}>
          {value}
        </div>
      </div>
    </div>
  );
};

const MetricsStrip = ({
  netPremium = null,
  maxProfit = null,
  maxLoss = null,
  pop = null,
  breakevens = [],
  unrealized = null,
}) => {
  const isCredit = Number.isFinite(netPremium) && netPremium < 0;
  const isDebit  = Number.isFinite(netPremium) && netPremium > 0;
  const breakLabel = breakevens?.length
    ? breakevens.map((b) => (Number.isFinite(b) ? `$${b.toFixed(2)}` : "—")).join(" / ")
    : "None";

  return (
    <div
      className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 bg-[#0E1A30] border border-white/10 rounded-xl overflow-hidden"
      data-testid="forge-metrics-strip"
    >
      <Tile
        icon={CircleDollarSign}
        label={isCredit ? "Net Credit" : isDebit ? "Net Debit" : "Net Premium"}
        value={fmt$(netPremium === null ? null : Math.abs(netPremium))}
        tone={isCredit ? "green" : isDebit ? "red" : "white"}
        testid="forge-metric-net"
      />
      <Tile
        icon={TrendingDown}
        label="Max Loss"
        value={fmt$(maxLoss === null ? null : -Math.abs(maxLoss))}
        tone="red"
        testid="forge-metric-maxloss"
      />
      <Tile
        icon={TrendingUp}
        label="Max Profit"
        value={fmt$(maxProfit)}
        tone="green"
        testid="forge-metric-maxprofit"
      />
      <Tile
        icon={PieChart}
        label="Chance of Profit"
        value={fmtPct(pop)}
        tone="blue"
        testid="forge-metric-pop"
      />
      <Tile
        icon={ArrowRightLeft}
        label="Breakevens"
        value={breakLabel}
        tone="white"
        testid="forge-metric-breakevens"
      />
      <Tile
        icon={Wallet}
        label="Unrealized P/L"
        value={fmt$(unrealized)}
        tone={
          unrealized === null
            ? "white"
            : unrealized >= 0
              ? "green"
              : "red"
        }
        testid="forge-metric-unrealized"
      />
    </div>
  );
};

export default MetricsStrip;
